import { ref } from 'vue';
import { AxiosError } from 'axios';
import { defineStore } from 'pinia';

import initApi from '@/api/initApiV2';

import {
  NotificationsApi,
  type NotificationsApiGetNotificationsRequest,
  type NotificationsApiBulkUpdateNotificationRequest,
  type NotificationsApiUpdateNotificationRequest,
  type PaginatedResultNotification,
} from '@/clients/v2';

import { handleApiError } from '@/api/handleApiError.ts';

export const useNotificationStore = defineStore('notification-v2', () => {
  const notificationsData = ref<PaginatedResultNotification>();
  const newNotificationCount = ref(0);

  const getNotifications = async (params: NotificationsApiGetNotificationsRequest) => {
    try {
      const notificationApi = await initApi(NotificationsApi);
      const response = await notificationApi?.getNotifications(params);

      if (!response) return;

      notificationsData.value = response.data;

      const unreadCount = response.data.extra?.unread_count;
      if (typeof unreadCount === 'number') {
        newNotificationCount.value = unreadCount;
      }

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getNotificationById = async (notificationId: string) => {
    try {
      const notificationApi = await initApi(NotificationsApi);
      const response = await notificationApi?.getNotification({ notificationId });

      return response?.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const updateNotification = async (params: NotificationsApiUpdateNotificationRequest) => {
    try {
      const notificationApi = await initApi(NotificationsApi);
      await notificationApi?.updateNotification(params);
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const bulkUpdateNotification = async (params: NotificationsApiBulkUpdateNotificationRequest) => {
    try {
      const notificationApi = await initApi(NotificationsApi);
      await notificationApi?.bulkUpdateNotification(params);
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const setNewNotifications = (count: number) => {
    newNotificationCount.value = count;
  };

  return {
    notificationsData,
    newNotificationCount,
    getNotifications,
    getNotificationById,
    updateNotification,
    bulkUpdateNotification,
    setNewNotifications,
  };
});
